/* ============================================
   COMPXSS — Motion Layer (Shopify build)
   Same motion as the original site, re-runnable per section so the
   theme editor can re-render a section without leaving it invisible.
   ============================================ */

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(hover: none)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* Scroll progress bar + grain overlay (once per page) */
  const progress = document.createElement('div');
  progress.className = 'scroll-progress';
  document.body.appendChild(progress);
  const updateProgress = () => {
    const h = document.documentElement;
    const max = h.scrollHeight - h.clientHeight;
    const scrolled = max > 0 ? h.scrollTop / max : 0;
    progress.style.transform = `scaleX(${Math.max(0, Math.min(1, scrolled))})`;
  };
  window.addEventListener('scroll', updateProgress, { passive: true });
  updateProgress();

  const grain = document.createElement('div');
  grain.className = 'grain';
  document.body.appendChild(grain);

  /* Reveal-on-scroll: [data-reveal] and children of [data-stagger] */
  const io = reduced ? null : new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  function initReveal(root) {
    root.querySelectorAll('[data-stagger]').forEach(parent => {
      Array.from(parent.children).forEach((child, i) => {
        if (!child.hasAttribute('data-reveal')) child.setAttribute('data-reveal', parent.dataset.stagger || 'up');
        child.style.transitionDelay = `${i * 80}ms`;
      });
    });
    const els = root.querySelectorAll('[data-reveal], [data-stagger] > *');
    // In the theme editor everything is shown at once: merchants need to see
    // what they are editing, not wait for a scroll trigger.
    if (reduced || designMode || !io) { els.forEach(el => el.classList.add('is-revealed')); return; }
    els.forEach(el => io.observe(el));
  }

  /* Letter-mask hero reveal */
  function splitLetters(root) {
    root.querySelectorAll('.reveal-letters').forEach(el => {
      if (el.dataset.split) return;
      el.dataset.split = '1';
      const text = el.textContent.trim();
      el.textContent = '';
      el.setAttribute('aria-label', text);
      [...text].forEach((char, i) => {
        const span = document.createElement('span');
        span.className = 'l';
        span.setAttribute('aria-hidden', 'true');
        span.style.setProperty('--i', i);
        const inner = document.createElement('span');
        inner.className = 'l-i';
        inner.textContent = char === ' ' ? ' ' : char;
        span.appendChild(inner);
        el.appendChild(span);
      });
    });
  }

  /* Magnetic buttons */
  function initMagnetic(root) {
    if (reduced || isTouch) return;
    root.querySelectorAll('.magnetic').forEach(btn => {
      if (btn.dataset.magnetic) return;
      btn.dataset.magnetic = '1';
      btn.addEventListener('mousemove', e => {
        const r = btn.getBoundingClientRect();
        btn.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.3}px, ${(e.clientY - r.top - r.height / 2) * 0.3}px)`;
      });
      btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
    });
  }

  /* Counters: data-counter="<target>" */
  function initCounters(root) {
    const counters = root.querySelectorAll('[data-counter]');
    if (!counters.length || reduced) return;
    const cio = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseFloat(el.dataset.counter);
        const start = performance.now();
        const fmt = el.dataset.format || '';
        const tick = (now) => {
          const t = Math.min(1, (now - start) / 1400);
          const val = Math.round(target * (1 - Math.pow(1 - t, 3)) * 100) / 100;
          el.textContent = fmt ? fmt.replace('{n}', val) : val;
          if (t < 1) requestAnimationFrame(tick);
          else el.textContent = fmt ? fmt.replace('{n}', target) : target;
        };
        requestAnimationFrame(tick);
        cio.unobserve(el);
      });
    }, { threshold: 0.5 });
    counters.forEach(el => cio.observe(el));
  }

  function initAll(root) {
    splitLetters(root);
    initReveal(root);
    initMagnetic(root);
    initCounters(root);
  }

  initAll(document);

  const markLoaded = () => document.body.classList.add('is-loaded');
  if (document.readyState === 'complete') markLoaded();
  else window.addEventListener('load', markLoaded);
  // never leave the page invisible if a slow asset stalls the load event
  setTimeout(markLoaded, 2500);

  /* Hero parallax + mouse-follow glow (queried live so a re-rendered hero still works) */
  if (!reduced) {
    let raf;
    window.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const heroBg = document.querySelector('.hero-bg');
        const heroContent = document.querySelector('.hero-content');
        const y = window.scrollY;
        if (!heroBg || y > window.innerHeight) return;
        heroBg.style.transform = `translate3d(0, ${y * 0.35}px, 0) scale(${1 + y * 0.0003})`;
        if (heroContent) {
          heroContent.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
          heroContent.style.opacity = `${Math.max(0, 1 - y / 600)}`;
        }
      });
    }, { passive: true });
  }
  if (!reduced && !isTouch) {
    document.addEventListener('mousemove', e => {
      const heroGlow = document.querySelector('.hero-glow');
      if (!heroGlow || window.scrollY > window.innerHeight) return;
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      heroGlow.style.background = `radial-gradient(circle at ${x}% ${y}%, rgba(255, 255, 255, 0.10), transparent 50%)`;
    });
  }

  /* Brand-mark rotation on scroll */
  if (!reduced) {
    let lastScroll = 0;
    let rot = 0;
    window.addEventListener('scroll', () => {
      rot += (window.scrollY - lastScroll) * 0.08;
      lastScroll = window.scrollY;
      document.querySelectorAll('.brand-mark img').forEach(m => { m.style.transform = `rotate(${rot}deg)`; });
    }, { passive: true });
  }

  /* Anchor smooth-scroll with header offset */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 80, behavior: reduced ? 'auto' : 'smooth' });
  });

  /* Theme editor: a section was re-rendered or added */
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  window.CXAnim = { initAll };
})();
